// =============================================================================
// Admin Conversion Page (Phase II: Convert Subscriptions to Investors)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import {
  CheckCircleIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  UserIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import type { ReadyForConversionItem, ConversionResult } from '../../../types/fundraising';
import { usePermissions } from '../../../hooks/usePermissions';

// =============================================================================
// Component
// =============================================================================

export function ConversionPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('fundraising', 'create');
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState<ReadyForConversionItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConverting, setIsConverting] = useState(false);
  const [conversionResults, setConversionResults] = useState<ConversionResult[] | null>(null);
  const [sendWelcomeEmails, setSendWelcomeEmails] = useState(true);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getReadyForConversion();
      setItems(data);
    } catch (err) {
      console.error('Failed to load conversion items:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((item) => item.id)));
    }
  };

  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleConvertSingle = async (subscriptionId: string) => {
    setIsConverting(true);
    setError(null);
    try {
      const result = await fundraisingService.convertSubscription({
        subscriptionId,
        sendWelcomeEmail: sendWelcomeEmails,
      });
      setConversionResults([result]);
      if (result.success) {
        setSelectedIds(new Set());
        loadItems(); // Refresh the list
      }
    } catch (err) {
      console.error('Failed to convert subscription:', err);
      setError(getApiError(err).message);
    } finally {
      setIsConverting(false);
    }
  };

  const handleBulkConvert = async () => {
    if (selectedIds.size === 0) return;

    setIsConverting(true);
    setError(null);
    try {
      const result = await fundraisingService.bulkConvertSubscriptions(
        Array.from(selectedIds),
        sendWelcomeEmails
      );
      setConversionResults([...result.successful, ...result.failed]);
      if (result.successful.length > 0) {
        setSelectedIds(new Set());
        loadItems(); // Refresh the list
      }
    } catch (err) {
      console.error('Failed to bulk convert subscriptions:', err);
      setError(getApiError(err).message);
    } finally {
      setIsConverting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalSelected = items
    .filter((item) => selectedIds.has(item.id))
    .reduce((sum, item) => sum + item.fundedAmount, 0);

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading && items.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Convert to Investors</h1>
          <p className="text-sm text-gray-500 mt-1">
            Convert funded subscriptions to full investor accounts
          </p>
        </div>
        <Button variant="secondary" onClick={loadItems} className="flex items-center gap-2">
          <ArrowPathIcon className="h-5 w-5" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <UserPlusIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Ready to Convert</p>
              <p className="text-2xl font-bold text-gray-900">{items.length}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Selected</p>
              <p className="text-2xl font-bold text-gray-900">{selectedIds.size}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Existing Users</p>
              <p className="text-2xl font-bold text-gray-900">
                {items.filter((i) => i.hasExistingUser).length}
              </p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <span className="text-purple-600 font-bold text-sm">$</span>
            </div>
            <div>
              <p className="text-sm text-gray-500">Selected Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalSelected)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Conversion Options */}
      <Card padding="sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={sendWelcomeEmails}
                onChange={(e) => setSendWelcomeEmails(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Send welcome emails with login credentials
              </span>
            </label>
          </div>
          {selectedIds.size > 0 && canCreate && (
            <Button onClick={handleBulkConvert} disabled={isConverting}>
              {isConverting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <UserPlusIcon className="h-5 w-5 mr-2" />
                  Convert {selectedIds.size} Selected
                </>
              )}
            </Button>
          )}
        </div>
      </Card>

      {/* Conversion Results */}
      {conversionResults && conversionResults.length > 0 && (
        <Card padding="sm">
          <div className="space-y-2">
            <h3 className="font-medium text-gray-900">Conversion Results</h3>
            {conversionResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg flex items-center gap-3 ${
                  result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {result.success ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  <ExclamationTriangleIcon className="h-5 w-5" />
                )}
                <span className="text-sm">{result.message}</span>
              </div>
            ))}
            <button
              onClick={() => setConversionResults(null)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Items List */}
      <Card padding="none">
        {items.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-green-300 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">All caught up!</h3>
            <p className="mt-2 text-sm text-gray-500">
              There are no funded subscriptions waiting to be converted.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="py-3 px-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === items.length && items.length > 0}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Contact</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Deal</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">
                    Funded Amount
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                    Funded Date
                  </th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">
                    Existing User
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      selectedIds.has(item.id) ? 'bg-primary-50' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => handleToggleSelect(item.id)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {item.contact.firstName} {item.contact.lastName}
                        </p>
                        <p className="text-xs text-gray-500">{item.contact.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{item.deal.name}</td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(item.fundedAmount)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {formatDate(item.fundedAt)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {item.hasExistingUser ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          No
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {canCreate && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleConvertSingle(item.id)}
                          disabled={isConverting}
                        >
                          Convert
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Help Text */}
      <Card padding="sm">
        <h3 className="font-medium text-gray-900 mb-2">About Conversion</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>
            <strong>New Users:</strong> A new user account will be created with a temporary password.
            The investor will receive a welcome email with login instructions.
          </li>
          <li>
            <strong>Existing Users:</strong> If an account already exists for the contact&apos;s email,
            the investment will be added to their existing account.
          </li>
          <li>
            <strong>Investor Profile:</strong> An investor profile matching the subscription&apos;s
            entity type will be created or linked.
          </li>
          <li>
            <strong>Investment Record:</strong> A new investment record will be created with the
            funded amount and ownership percentage.
          </li>
        </ul>
      </Card>
    </div>
  );
}

export default ConversionPage;
